import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'not started', label: 'Not Started' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'delivery completed', label: 'Delivery Completed' },
  { value: 'batch completed', label: 'Batch Completed' },
];

export default function BatchesWithoutStudentsNotifications() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchCourse, setSearchCourse] = useState('');
  const [searchBatch, setSearchBatch] = useState('');
  const [searchInternal, setSearchInternal] = useState('');
  const [searchWhatsapp, setSearchWhatsapp] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await axiosInstance.get('/admin/batches/without-students');
        if (cancelled) return;
        setBatches(resp.data?.batches || []);
      } catch (err) {
        if (!cancelled) {
          // Prefer a concise, user-friendly message
          const message = err?.response?.data?.error || 'Failed to load batches without students.';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredBatches = useMemo(() => {
    const courseTerm = searchCourse.trim().toLowerCase();
    const batchTerm = searchBatch.trim().toLowerCase();
    const internalTerm = searchInternal.trim().toLowerCase();
    const whatsappTerm = searchWhatsapp.trim().toLowerCase();

    return (batches || []).filter((batch) => {
      if (statusFilter !== 'all' && batch.status !== statusFilter) {
        return false;
      }

      const courseShort = String(batch.courseShortName || '').toLowerCase();
      const courseFull = String(batch.courseFullName || '').toLowerCase();
      const batchName = String(batch.batchName || '').toLowerCase();
      const internalName = String(batch.batchInternalName || '').toLowerCase();
      const whatsapp = String(batch.whatsappGroupName || '').toLowerCase();

      if (courseTerm && !courseShort.includes(courseTerm) && !courseFull.includes(courseTerm)) {
        return false;
      }
      if (batchTerm && !batchName.includes(batchTerm)) {
        return false;
      }
      if (internalTerm && !internalName.includes(internalTerm)) {
        return false;
      }
      if (whatsappTerm && !whatsapp.includes(whatsappTerm)) {
        return false;
      }

      return true;
    });
  }, [batches, statusFilter, searchCourse, searchBatch, searchInternal, searchWhatsapp]);

  const hasData = filteredBatches.length > 0;

  const handleDownloadExcel = async () => {
    if (!filteredBatches.length) {
      return;
    }

    try {
      const XLSX = await import('xlsx');

      const headers = [
        'Course Short Name',
        'Course Full Name',
        'Batch Name',
        'Batch ID',
        'Batch Internal Name',
        'WhatsApp Group',
        'Status',
        'Registration Opening Date',
        'Registration Closing Date',
        'Batch Starting Date',
        'Batch Ending Date',
      ];

      const rows = filteredBatches.map((batch) => [
        batch.courseShortName || '',
        batch.courseFullName || '',
        batch.batchName || '',
        batch.batchId || '',
        batch.batchInternalName || '',
        batch.whatsappGroupName || '',
        batch.status || '',
        batch.registrationOpeningDate || '',
        batch.registrationClosingDate || '',
        batch.batchStartingDate || '',
        batch.batchEndingDate || '',
      ]);

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Batches Without Students');

      const today = new Date().toISOString().split('T')[0];
      const fileName = `Batches_Without_Students_${today}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error('Failed to download batches without students Excel', err);
    }
  };

  return (
    <DashboardLayout title="Batches Without Students">
      <div className="space-y-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Batches Without Students</h1>
            <p className="mt-1 text-sm text-gray-600">
              List of batches that currently do not have any student registrations recorded.
            </p>
          </div>
          <div className="space-y-2 text-right">
            <div className="inline-flex items-center rounded-full bg-indigo-50 px-4 py-1.5 text-xs font-medium text-indigo-700">
              <span className="mr-2 uppercase tracking-wide">Matching Batches</span>
              <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                {filteredBatches.length}
              </span>
            </div>
            {batches.length > 0 && (
              <div className="text-[11px] text-gray-500">
                Showing {filteredBatches.length} of {batches.length} batches without students
              </div>
            )}
            {hasData && (
              <button
                type="button"
                onClick={handleDownloadExcel}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-xs font-medium text-white shadow-sm transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download as Excel
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500 shadow-sm">
            Loading batches without students...
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <div className="grid gap-3 md:grid-cols-5">
                <div className="md:col-span-1">
                  <label className="block text-xs font-medium text-gray-600">Status</label>
                  <select
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs text-gray-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Course</label>
                  <input
                    type="text"
                    value={searchCourse}
                    onChange={(e) => setSearchCourse(e.target.value)}
                    placeholder="Search by course"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Batch</label>
                  <input
                    type="text"
                    value={searchBatch}
                    onChange={(e) => setSearchBatch(e.target.value)}
                    placeholder="Search by batch name"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Internal Name</label>
                  <input
                    type="text"
                    value={searchInternal}
                    onChange={(e) => setSearchInternal(e.target.value)}
                    placeholder="Search by internal name"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">WhatsApp Group</label>
                  <input
                    type="text"
                    value={searchWhatsapp}
                    onChange={(e) => setSearchWhatsapp(e.target.value)}
                    placeholder="Search by WhatsApp group"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {hasData ? (
              <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Course
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Batch
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Internal Name
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        WhatsApp Group
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Status
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Registration Period
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Batch Duration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {filteredBatches.map((batch) => (
                      <tr key={batch.batchId} className="hover:bg-gray-50">
                        <td className="px-4 py-2 align-top">
                          <div className="font-medium text-gray-900">{batch.courseShortName || 'N/A'}</div>
                          {batch.courseFullName && (
                            <div className="mt-0.5 text-xs text-gray-500">{batch.courseFullName}</div>
                          )}
                        </td>
                        <td className="px-4 py-2 align-top">
                          <div className="font-medium text-gray-900">{batch.batchName}</div>
                          <div className="mt-0.5 text-xs text-gray-400">ID: {batch.batchId}</div>
                        </td>
                        <td className="px-4 py-2 align-top text-sm text-gray-700">
                          {batch.batchInternalName || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-2 align-top text-sm text-gray-700">
                          {batch.whatsappGroupName || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-2 align-top">
                          <span className="inline-flex rounded-full bg-gray-100 px-3 py-0.5 text-xs font-medium capitalize text-gray-800">
                            {batch.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 align-top text-xs text-gray-700">
                          <div>{batch.registrationOpeningDate || '—'}</div>
                          <div className="text-gray-400">to</div>
                          <div>{batch.registrationClosingDate || '—'}</div>
                        </td>
                        <td className="px-4 py-2 align-top text-xs text-gray-700">
                          <div>{batch.batchStartingDate || '—'}</div>
                          <div className="text-gray-400">to</div>
                          <div>{batch.batchEndingDate || '—'}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600 shadow-sm">
                {batches.length === 0
                  ? 'No batches without students were found. You can adjust filters once data is available.'
                  : 'No batches match the current filters. Try adjusting your search or status.'}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
